import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    useTheme as useMuiTheme,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Tooltip,
    TablePagination,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCities, fetchZones, setFilters, clearFilters, toggleCityStatus } from '../../store/slices/citySlice';
import { useTheme } from '../../contexts/ThemeContext';
import CityForm from './CityForm';
import CityView from './CityView';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

interface City {
    id: number;
    name: string;
    code: string;
    zone: string;
    status: number;
    created_at: string;
    updated_at: string;
}

const Cities = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { cities, pagination, filters, zones, loading, error } = useAppSelector((state) => state.cities);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const { toast, showSuccess, showError, hideToast } = useToast();

    // Local state
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [cityToDelete, setCityToDelete] = useState<City | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [zoneFilter, setZoneFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Load cities and zones on component mount
    useEffect(() => {
        loadCities();
        dispatch(fetchZones());
    }, [page, rowsPerPage, filters]);

    const loadCities = () => {
        const params = {
            page: page + 1,
            per_page: rowsPerPage,
            query: filters.query,
            status: filters.status,
            zone: filters.zone
        };
        dispatch(fetchCities(params));
    };

    // Handle search
    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setPage(0);
    };

    // Handle status filter
    const handleStatusFilter = (status: string) => {
        setStatusFilter(status);
        dispatch(setFilters({ status }));
        setPage(0);
    };

    // Handle zone filter
    const handleZoneFilter = (zone: string) => {
        setZoneFilter(zone);
        dispatch(setFilters({ zone }));
        setPage(0);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setStatusFilter('');
        setZoneFilter('');
        dispatch(clearFilters());
        setPage(0);
    };

    // Handle pagination
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle city actions
    const handleViewCity = (city: City) => {
        setSelectedCity(city);
        setOpenView(true);
    };

    const handleEditCity = (city: City) => {
        setSelectedCity(city);
        setOpenForm(true);
    };

    const handleDeleteCity = (city: City) => {
        setCityToDelete(city);
        setDeleteDialog(true);
    };

    const handleAddCity = () => {
        setSelectedCity(null);
        setOpenForm(true);
    };

    const handleFormClose = (isSuccess?: boolean, isEdit?: boolean, cityName?: string) => {
        setOpenForm(false);
        setSelectedCity(null);
        if (isSuccess && cityName) {
            loadCities();
            if (isEdit) {
                showSuccess(`City "${cityName}" has been updated successfully`);
            } else {
                showSuccess(`City "${cityName}" has been created successfully`);
            }
        }
    };

    const handleFormError = (message: string) => {
        showError(message);
    };

    const handleViewClose = () => {
        setOpenView(false);
        setSelectedCity(null);
    };

    const handleDeleteConfirm = () => {
        // TODO: Implement delete functionality
        setDeleteDialog(false);
        setCityToDelete(null);
        loadCities();
    };

    const handleToggleStatus = async (city: City) => {
        try {
            const result = await dispatch(toggleCityStatus(city.id)).unwrap();
            const newStatus = result.status === 1 ? 'activated' : 'deactivated';
            showSuccess(`City "${city.name}" has been ${newStatus} successfully`);
            loadCities();
        } catch (err: any) {
            showError(err?.message || 'Failed to update city status');
        }
    };

    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                icon={<ActiveIcon />}
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                icon={<InactiveIcon />}
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    if (loading && cities.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 2 : 0
            }}>
                <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    sx={{ 
                        color: appTheme.palette.text.primary, 
                        fontWeight: 600,
                        textAlign: isMobile ? 'center' : 'left'
                    }}
                >
                    Cities
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCity}
                    sx={{
                        bgcolor: appTheme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: appTheme.palette.secondary.dark,
                        }
                    }}
                >
                    {isMobile ? 'Add City' : 'Add New City'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: muiTheme.palette.background.paper }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: appTheme.palette.text.primary }}>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search by name or code"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleSearch}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilter(e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="1">Active</MenuItem>
                                    <MenuItem value="0">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Zone</InputLabel>
                                <Select
                                    value={zoneFilter}
                                    onChange={(e) => handleZoneFilter(e.target.value)}
                                    label="Zone"
                                >
                                    <MenuItem value="">All Zones</MenuItem>
                                    {zones.map((zone) => (
                                        <MenuItem key={zone} value={zone}>
                                            {zone}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                fullWidth
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Cities Table */}
            <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: muiTheme.palette.action.hover }}>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Zone</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cities.map((city) => (
                                <TableRow key={city.id} hover>
                                    <TableCell>{city.name}</TableCell>
                                    <TableCell>{city.code}</TableCell>
                                    <TableCell>{city.zone}</TableCell>
                                    <TableCell>{getStatusChip(city.status)}</TableCell>
                                    <TableCell>
                                        {new Date(city.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewCity(city)}
                                                    sx={{ color: muiTheme.palette.info.main }}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit City">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditCity(city)}
                                                    sx={{ color: muiTheme.palette.primary.main }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={city.status ? 'Deactivate' : 'Activate'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleStatus(city)}
                                                    sx={{ 
                                                        color: city.status 
                                                            ? muiTheme.palette.error.main 
                                                            : muiTheme.palette.success.main 
                                                    }}
                                                >
                                                    {city.status ? <InactiveIcon /> : <ActiveIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete City">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteCity(city)}
                                                    sx={{ color: muiTheme.palette.error.main }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Card>

            {/* City Form Dialog */}
            <CityForm
                open={openForm}
                city={selectedCity || undefined}
                mode={selectedCity ? 'edit' : 'create'}
                onClose={handleFormClose}
                onError={handleFormError}
            />

            {/* City View Dialog */}
            {selectedCity && (
                <CityView
                    open={openView}
                    city={selectedCity}
                    onClose={handleViewClose}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete City"
                message={`Are you sure you want to delete "${cityToDelete?.name}"? This action cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => {
                    setDeleteDialog(false);
                    setCityToDelete(null);
                }}
            />

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default Cities;
